type TRadioButtonProps = {
  name: string;
  value: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
  disabled?: boolean;
};

type TRadioOption = {
  value: string;
  label: string;
};

type TRadioButtonGroupProps = {
  name: string;
  label?: string;
  labelClassName?: string;
  options: TRadioOption[];
  value: string;
  onChange: (value: string) => void;
  direction?: 'row' | 'column';
  gap?: string;
  formatLabel?: (label: string) => string;
  error?: string;
  disabled?: boolean;
};

const RadioButton: React.FC<TRadioButtonProps> = ({
  name,
  value,
  checked,
  onChange,
  label,
  disabled,
}) => (
  <label
    className={`relative flex items-center select-none text-lg w-fit ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="absolute opacity-0 cursor-pointer"
    />
    <span
      className={`relative inline-block w-5 h-5 bg-white border rounded-full transition-colors duration-200 ease-in-out
        ${checked ? 'bg-brand-default! border-brand-default' : 'border-neutral-light-200'} ${!disabled && 'hover:bg-neutral-light-50'}`}
    >
      <span
        className={`absolute w-2.5 h-2.5 bg-white rounded-full transition-opacity duration-200 ease-in-out
          ${checked ? 'opacity-100' : 'opacity-0'} left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2`}
      ></span>
    </span>
    <span className="ms-1.5 text-sm font-medium text-neutral-primary">
      {label}
    </span>
  </label>
);

export const RadioButtonGroup: React.FC<TRadioButtonGroupProps> = ({
  name,
  label,
  labelClassName,
  options,
  value,
  onChange,
  direction = 'row',
  gap = 'gap-6',
  formatLabel,
  error,
  disabled,
}) => (
  <div className="flex flex-col gap-4">
    {label && (
      <label
        className={`block text-sm font-medium text-neutral-primary leading-5 capitalize${labelClassName ? ` ${labelClassName}` : ''}`}
      >
        {label}
      </label>
    )}
    <div
      className={`flex ${direction === 'row' ? 'flex-row' : 'flex-col'} ${gap} items-center`}
    >
      {options.map((option) => (
        <RadioButton
          key={option.value}
          name={name}
          value={option.value}
          label={formatLabel ? formatLabel(option.label) : option.label}
          checked={value === option.value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      ))}
    </div>
    {error && <span className="text-xs text-danger-default">{error}</span>}
  </div>
);
